import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import {
  useDocumentTemplates,
  useDocuments,
  useGenerateDocument,
  useSendDocument,
  useSignDocument,
  useUpdateDocument,
  useUploadDocument,
  useDeleteDocument,
  aiFillDocument,
  documentFileUrl,
  type DocumentTemplate,
  type DocumentRecord,
} from '@/lib/documents';
import { MarkdownView } from '@/components/app/MarkdownView';
import { useEmployees } from '@/lib/employees';
import { useMyCompany } from '@/lib/companies';
import { useDepartments } from '@/lib/departments';
import { countryFlag, COUNTRIES } from '@/lib/countries';
import { confirm } from '@/components/ui/ConfirmDialog';
import { VariableField } from '@/components/app/VariableField';
import { useCreateApproval } from '@/lib/approvals';
import { useCurrentUser } from '@/lib/auth';

const COUNTRY_NAME: Record<string, string> = Object.fromEntries(COUNTRIES.map((c) => [c.code, c.englishName]));

export function DocumentsPage() {
  const { t } = useTranslation();
  const templates = useDocumentTemplates();
  const employees = useEmployees();

  // Group the template list by country (global last) so people pick by jurisdiction.
  const templatesByCountry = useMemo(() => {
    const groups = new Map<string, DocumentTemplate[]>();
    for (const tpl of templates.data ?? []) {
      const k = tpl.countryCode || '';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(tpl);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === '') return 1;
      if (b[0] === '') return -1;
      return (COUNTRY_NAME[a[0]] ?? a[0]).localeCompare(COUNTRY_NAME[b[0]] ?? b[0]);
    });
  }, [templates.data]);
  const [employeeFilter, setEmployeeFilter] = useState<string>('');
  const documents = useDocuments(employeeFilter ? { employeeId: employeeFilter } : {});
  const [creating, setCreating] = useState<{ template?: DocumentTemplate } | null>(null);
  const [preview, setPreview] = useState<DocumentRecord | null>(null);
  const [editing, setEditing] = useState<DocumentRecord | null>(null);
  const upload = useUploadDocument();
  const fileRef = useRef<HTMLInputElement | null>(null);

  function onUploadPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) upload.mutate({ file: f });
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('documents.title')}</h1>
            <p className="mt-1 text-sm text-ink-500">{t('documents.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onUploadPick} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="inline-flex items-center px-4 py-2 rounded-lg border border-ink-200 text-ink-800 text-sm font-semibold hover:bg-ink-50 disabled:opacity-50 transition"
            >
              {upload.isPending ? t('documents.uploading', { defaultValue: 'Uploading…' }) : t('documents.upload', { defaultValue: 'Upload file' })}
            </button>
            <button
              onClick={() => setCreating({})}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
            >
              {t('documents.newDocument')}
            </button>
          </div>
        </div>

        <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <aside className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50">
              <h2 className="text-sm font-semibold text-ink-700">{t('documents.templatesTitle')}</h2>
              <p className="text-[10px] text-ink-500 mt-0.5">{t('documents.templatesHint')}</p>
            </div>
            <ul className="divide-y divide-ink-100 max-h-[70vh] overflow-y-auto">
              {templates.isLoading && <li className="px-4 py-3 text-xs text-ink-500">{t('documents.loading')}</li>}
              {templatesByCountry.map(([code, items]) => (
                <li key={code || 'global'}>
                  <div className="sticky top-0 z-10 px-4 py-1.5 bg-ink-100/90 backdrop-blur-sm flex items-center gap-2 border-b border-ink-200">
                    <span className="text-sm">{code ? countryFlag(code) : '🌐'}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-600">
                      {code ? (COUNTRY_NAME[code] ?? code) : t('documents.globalGroup', { defaultValue: 'Global' })}
                    </span>
                    <span className="text-[10px] text-ink-400">· {items.length}</span>
                  </div>
                  <ul className="divide-y divide-ink-100">
                    {items.map((tpl) => (
                      <li key={tpl.templateKey}>
                        <button
                          onClick={() => setCreating({ template: tpl })}
                          className="block w-full text-start px-4 py-3 hover:bg-ink-50 transition"
                        >
                          <p className="text-sm font-medium text-ink-900">{tpl.title}</p>
                          <p className="text-[10px] text-ink-500 mt-0.5">{tpl.category ?? 'Other'} · {tpl.variables.length} variables</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </aside>

          <main className="lg:col-span-2 rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-sm font-semibold text-ink-700">{t('documents.recentTitle')}</h2>
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-600 font-medium" htmlFor="doc-employee-filter">
                  {t('documents.filterEmployee', { defaultValue: 'Employee' })}
                </label>
                <select
                  id="doc-employee-filter"
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className="border border-border bg-card rounded-md px-2 py-1 text-xs text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500"
                >
                  <option value="">{t('documents.filterAll', { defaultValue: 'All' })}</option>
                  {(employees.data ?? []).map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.legalNameLocal}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white text-ink-500">
                  <tr>
                    <th className="text-start font-medium px-4 py-2">{t('documents.col_title')}</th>
                    <th className="text-start font-medium px-4 py-2">{t('documents.col_employee')}</th>
                    <th className="text-start font-medium px-4 py-2">{t('documents.col_status')}</th>
                    <th className="text-start font-medium px-4 py-2">{t('documents.col_created')}</th>
                    <th className="text-end font-medium px-4 py-2">{t('documents.col_action')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {documents.isLoading && <tr><td colSpan={5} className="text-center text-ink-500 py-8">{t('documents.loading')}</td></tr>}
                  {documents.data?.length === 0 && <tr><td colSpan={5} className="text-center text-ink-500 py-8">{t('documents.empty')}</td></tr>}
                  {(documents.data ?? []).map((d) => (
                    <Row
                      key={d.id}
                      d={d}
                      employeeId={d.employeeId ?? null}
                      employeeName={d.employeeId ? employees.data?.find((e) => e.id === d.employeeId)?.legalNameLocal ?? d.employeeId.slice(0, 8) : null}
                      onPreview={() => setPreview(d)}
                      onEdit={() => setEditing(d)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </main>
        </section>
      </div>

      {creating !== null && (
        <GenerateDialog
          initialTemplate={creating.template}
          templates={templates.data ?? []}
          employees={employees.data ?? []}
          onClose={() => setCreating(null)}
          onSuccess={(d) => { setCreating(null); setPreview(d); }}
        />
      )}
      {preview && <PreviewDialog d={preview} onClose={() => setPreview(null)} />}
      {editing && <EditDocumentDialog d={editing} onClose={() => setEditing(null)} />}
    </AppShell>
  );
}

function Row({
  d, employeeId, employeeName, onPreview, onEdit,
}: {
  d: DocumentRecord;
  employeeId: string | null;
  employeeName: string | null;
  onPreview: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const send = useSendDocument();
  const del = useDeleteDocument();
  const createApproval = useCreateApproval();
  const currentUser = useCurrentUser();
  const currentUserId = currentUser.data?.id;
  return (
    <tr className="hover:bg-ink-50/40">
      <td className="px-4 py-3">
        <p className="font-medium text-ink-900">{d.title}</p>
        <p className="text-[10px] text-ink-500 font-mono">{d.templateKey ?? '—'}</p>
      </td>
      <td className="px-4 py-3 text-ink-700">
        {employeeId && employeeName
          ? <Link to={`/app/people/${employeeId}`} className="text-accent-600 hover:underline">{employeeName}</Link>
          : employeeName ?? '—'}
      </td>
      <td className="px-4 py-3"><StatusPill status={d.status} /></td>
      <td className="px-4 py-3 num text-[11px] text-ink-500">{new Date(d.createdAt).toLocaleDateString()}</td>
      <td className="px-4 py-3 text-end">
        <div className="inline-flex items-center justify-end gap-1.5">
          {d.hasFile
            ? <a href={documentFileUrl(d.id)} target="_blank" rel="noopener" className="px-2 py-1 rounded-md bg-ink-100 text-ink-700 text-xs font-semibold hover:bg-ink-200 transition">{t('documents.download', { defaultValue: 'Download' })}</a>
            : <button onClick={onPreview} className="px-2 py-1 rounded-md bg-ink-100 text-ink-700 text-xs font-semibold hover:bg-ink-200 transition">{t('documents.preview')}</button>}
          {d.status === 'draft' && (
            <>
              <button onClick={onEdit} className="px-2 py-1 rounded-md bg-ink-100 text-ink-700 text-xs font-semibold hover:bg-ink-200 transition">{t('documents.edit', { defaultValue: 'Edit' })}</button>
              <button onClick={() => send.mutate(d.id)} className="px-2 py-1 rounded-md bg-accent-50 text-accent-700 text-xs font-semibold hover:bg-accent-500 hover:text-white transition">{t('documents.send')}</button>
            </>
          )}
          {(d.status === 'draft' || d.status === 'sent') && currentUserId && (
            <button
              disabled={createApproval.isPending}
              onClick={() => createApproval.mutate({
                targetKind: 'document',
                targetId: d.id,
                requiredSigners: [currentUserId],
              })}
              className="px-2 py-1 rounded-md bg-ok-50 text-ok-600 text-xs font-semibold hover:bg-ok-100 disabled:opacity-50 transition"
            >
              {t('documents.sendForApproval', { defaultValue: 'Send for approval' })}
            </button>
          )}
          {d.status !== 'signed' && (
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: t('documents.confirmDeleteTitle', { defaultValue: 'Delete document?' }),
                  description: t('documents.confirmDeleteBody', { defaultValue: `Permanently delete "${d.title}"?`, title: d.title }),
                  tone: 'destructive',
                  confirmLabel: t('documents.delete', { defaultValue: 'Delete' }),
                });
                if (ok) del.mutate(d.id);
              }}
              className="px-2 py-1 rounded-md text-rose-600 text-xs font-semibold hover:bg-rose-50 transition"
            >
              {t('documents.delete', { defaultValue: 'Delete' })}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function StatusPill({ status }: { status: DocumentRecord['status'] }) {
  const map: Record<DocumentRecord['status'], string> = {
    draft:    'bg-ink-100 text-ink-700',
    sent:     'bg-accent-50 text-accent-700',
    signed:   'bg-ok-50 text-ok-600',
    filed:    'bg-ok-50 text-ok-600',
    archived: 'bg-ink-100 text-ink-400',
    rejected: 'bg-warn-50 text-warn-600',
  };
  return <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${map[status]}`}>{status}</span>;
}

function GenerateDialog({
  initialTemplate,
  templates,
  employees,
  onClose,
  onSuccess,
}: {
  initialTemplate?: DocumentTemplate;
  templates: DocumentTemplate[];
  employees: Array<{ id: string; legalNameLocal: string; countryCode: string }>;
  onClose: () => void;
  onSuccess: (d: DocumentRecord) => void;
}) {
  const { t } = useTranslation();
  const [templateKey, setTemplateKey] = useState<string>(initialTemplate?.templateKey ?? templates[0]?.templateKey ?? '');
  const tpl = useMemo(() => templates.find((x) => x.templateKey === templateKey), [templates, templateKey]);
  const [employeeId, setEmployeeId] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [bindings, setBindings] = useState<Record<string, string>>({});
  const generate = useGenerateDocument();
  const allEmployees = useEmployees();
  const company = useMyCompany();
  const departments = useDepartments();
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Group templates by category for the picker.
  const grouped = useMemo(() => {
    const m = new Map<string, DocumentTemplate[]>();
    for (const x of templates) {
      const k = x.category || 'Other';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(x);
    }
    return Array.from(m.entries());
  }, [templates]);

  // Map an employee's record onto the template's known variable keys.
  function autofillFrom(empId: string, key = templateKey) {
    const template = templates.find((x) => x.templateKey === key);
    const emp = allEmployees.data?.find((e) => e.id === empId);
    if (!template || !emp) return;
    const mgr = emp.managerId ? allEmployees.data?.find((e) => e.id === emp.managerId) : null;
    const nameOf = (x: typeof emp) => x.preferredName || x.legalNameRoman || x.legalNameLocal;
    const money = emp.baseSalaryCents != null
      ? (emp.baseSalaryCents / 100).toLocaleString('en-US', emp.salaryCurrency === 'JPY' ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2 })
      : '';
    const map: Record<string, string> = {
      company_name: company.data?.name ?? '',
      employee_name: nameOf(emp),
      job_title: emp.jobTitle ?? '',
      start_date: emp.hireDate?.slice(0, 10) ?? '',
      hire_date: emp.hireDate?.slice(0, 10) ?? '',
      end_date: emp.terminationDate?.slice(0, 10) ?? '',
      base_salary: money,
      base_salary_usd: money,
      pay_frequency: emp.salaryPeriod ?? '',
      work_location: emp.workLocation ?? '',
      employment_type: emp.employmentType ?? '',
      department: (emp.departmentId ? departments.data?.find((d) => d.id === emp.departmentId)?.name : '') ?? '',
      reporting_manager: mgr ? nameOf(mgr) : '',
      today_date: new Date().toISOString().slice(0, 10),
    };
    const fill: Record<string, string> = {};
    for (const v of template.variables) if (map[v]) fill[v] = map[v];
    setBindings((prev) => ({ ...prev, ...fill }));
  }

  async function draftWithAi() {
    setAiBusy(true); setAiNote(null);
    try {
      const res = await aiFillDocument({ templateKey, employeeId: employeeId || undefined });
      setBindings((prev) => ({ ...prev, ...res.bindings }));
      setAiNote(res.aiUsed
        ? t('documents.aiDrafted', { defaultValue: 'AI drafted the remaining fields — review before sending.' })
        : t('documents.aiFactualOnly', { defaultValue: 'Filled from the employee record (AI not configured).' }));
    } catch {
      setAiNote(t('documents.aiFailed', { defaultValue: 'Could not draft with AI.' }));
    } finally {
      setAiBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const d = await generate.mutateAsync({
        templateKey,
        employeeId: employeeId || undefined,
        title: title.trim() || undefined,
        bindings,
      });
      onSuccess(d);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || t('documents.dialog_errorGeneric');
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-xl w-full p-6 max-h-[85vh] overflow-y-auto space-y-4">
        <h2 className="text-lg font-semibold text-ink-900">{t('documents.dialog_title')}</h2>

        <Field label={t('documents.dialog_template')}>
          <select
            value={templateKey}
            onChange={(e) => { setTemplateKey(e.target.value); if (employeeId) autofillFrom(employeeId, e.target.value); }}
            className="input"
          >
            {grouped.map(([cat, items]) => (
              <optgroup key={cat} label={cat}>
                {items.map((x) => (
                  <option key={x.templateKey} value={x.templateKey}>{x.countryCode ? `${countryFlag(x.countryCode)} ` : '🌐 '}{x.title}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>

        <Field label={t('documents.dialog_employee')}>
          <select
            value={employeeId}
            onChange={(e) => { setEmployeeId(e.target.value); if (e.target.value) autofillFrom(e.target.value); }}
            className="input"
          >
            <option value="">—</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.legalNameLocal} ({emp.countryCode})</option>)}
          </select>
          {employeeId && <p className="text-[10px] text-ink-500 mt-1">{t('documents.autofillHint', { defaultValue: 'Fields below were pre-filled from this employee’s record.' })}</p>}
        </Field>

        <Field label={t('documents.dialog_titleField')}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder={tpl?.title} />
        </Field>

        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-ink-700">{t('documents.dialog_variables')}</p>
            <button
              type="button"
              onClick={draftWithAi}
              disabled={aiBusy || !templateKey}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-xs font-semibold shadow-sm hover:opacity-90 disabled:opacity-50 transition"
            >
              {aiBusy ? t('documents.aiDrafting', { defaultValue: 'Drafting…' }) : `✨ ${t('documents.aiDraft', { defaultValue: 'Draft with AI' })}`}
            </button>
          </div>
          {aiNote && <p className="text-[10px] text-emerald-600 mb-2">{aiNote}</p>}
          <p className="text-[10px] text-ink-500 mb-3">{t('documents.dialog_missingVarsHint')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {(tpl?.variables ?? []).map((varName) => (
              <VariableField
                key={varName}
                name={varName}
                value={bindings[varName] ?? ''}
                onChange={(v) => setBindings({ ...bindings, [varName]: v })}
              />
            ))}
          </div>
        </div>

        {error && <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('common.cancel')}</button>
          <button type="submit" disabled={!templateKey || generate.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
            {generate.isPending ? t('documents.dialog_generating') : t('documents.dialog_generate')}
          </button>
        </div>

        <style>{`
          .input, .doc-var-input { width: 100%; border: 1px solid #D1D5DB; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; background: white; outline: none; transition: border-color 0.15s; font-family: inherit; }
          .input:focus, .doc-var-input:focus { border-color: #1A1AFF; box-shadow: 0 0 0 3px rgba(26,26,255,0.1); }
          textarea.doc-var-input { resize: vertical; min-height: 4.5rem; line-height: 1.5; }
        `}</style>
      </form>
    </div>
  );
}

function PreviewDialog({ d, onClose }: { d: DocumentRecord; onClose: () => void }) {
  const { t } = useTranslation();
  const sign = useSignDocument();
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-ink-100 bg-ink-50 flex items-baseline justify-between gap-3 sticky top-0">
          <h2 className="text-lg font-semibold text-ink-900">{d.title}</h2>
          <span className="text-[10px] text-ink-500 font-mono">{d.templateKey} · {d.status}</span>
        </div>
        <div className="p-6">
          {d.renderedMd
            ? <MarkdownView>{d.renderedMd}</MarkdownView>
            : <p className="text-sm text-ink-500 italic">—</p>}
          {d.signedAt && (
            <div className="mt-4 pt-4 border-t border-ink-100 text-xs text-ink-500">
              {d.signatures.map((s, i) => (
                <p key={i}>{t('documents.preview_signedBy', { name: s.signer_name, time: new Date(s.signed_at).toLocaleString() })} · hash {s.document_hash.slice(0, 12)}…</p>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-ink-100 bg-ink-50 flex justify-end gap-2 sticky bottom-0">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('documents.preview_close')}</button>
          {(d.status === 'draft' || d.status === 'sent') && (
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: t('documents.signDialog_title'),
                  description: t('documents.signDialog_consent'),
                  confirmLabel: t('documents.signDialog_signNow'),
                });
                if (!ok) return;
                await sign.mutateAsync(d.id);
                onClose();
              }}
              className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
            >
              {sign.isPending ? t('documents.signDialog_signing') : t('documents.signDialog_iAgree')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

/**
 * Edit a draft document. Two-pane layout — markdown textarea on the
 * left, live MarkdownView preview on the right — so reviewers can
 * tweak wording in context. Locked once the document has been sent
 * or signed (server enforces too).
 */
function EditDocumentDialog({ d, onClose }: { d: DocumentRecord; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateDocument();
  const [title, setTitle] = useState(d.title);
  const [body, setBody] = useState(d.renderedMd ?? '');
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        id: d.id,
        title: title.trim() || undefined,
        renderedMd: body,
      });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Failed to save changes';
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl shadow-glow border border-ink-200 w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">
        <header className="px-6 py-4 border-b border-ink-100 bg-ink-50 flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink-900">
            {t('documents.edit_title', { defaultValue: 'Edit draft' })}
          </h2>
          <span className="text-[10px] text-ink-500 font-mono">{d.templateKey} · {d.status}</span>
        </header>

        <div className="px-6 pt-4">
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">
              {t('documents.dialog_titleField', { defaultValue: 'Title' })}
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
            />
          </label>
        </div>

        <div className="px-6 py-4 flex-1 min-h-0 grid md:grid-cols-2 gap-4">
          <div className="flex flex-col min-h-[400px]">
            <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-1">
              {t('documents.edit_markdown', { defaultValue: 'Markdown (editable)' })}
            </p>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="flex-1 w-full border border-ink-300 rounded-lg px-3 py-2 text-xs font-mono leading-relaxed resize-none focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
              spellCheck={false}
            />
          </div>
          <div className="flex flex-col min-h-[400px] border border-ink-200 rounded-lg bg-ink-50/30 overflow-y-auto">
            <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold sticky top-0 bg-ink-50/95 backdrop-blur-sm px-4 py-2 border-b border-ink-100">
              {t('documents.edit_preview', { defaultValue: 'Live preview' })}
            </p>
            <div className="p-4">
              {body
                ? <MarkdownView>{body}</MarkdownView>
                : <p className="text-xs text-ink-400 italic">{t('documents.edit_emptyPreview', { defaultValue: 'Type in the left pane to see the rendered document.' })}</p>}
            </div>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-3 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>
        )}

        <footer className="px-6 py-4 border-t border-ink-100 bg-ink-50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={update.isPending}
            className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
          >
            {update.isPending
              ? t('documents.edit_saving', { defaultValue: 'Saving…' })
              : t('documents.edit_save', { defaultValue: 'Save changes' })}
          </button>
        </footer>
      </form>
    </div>
  );
}
